/**
 * Module 'setup' (ADR 0047, R-PL-3): the assistant-led setup. Detection and defaults are code: a
 * pure `proposeSetup(input)` turns what Orgus detected into a SetupPlan, a typed list of catalogue
 * calls. A person approves the whole plan once (a plan-bound intent bound to its hash), then
 * every step runs through the normal gate as `agent:assistant` on behalf of the approver, never
 * above the approver's grants at that step's scope. Secrets and consent are handoffs a person
 * clicks, never the model's.
 *
 * This file is the contract and the pure planner: `proposeSetup`, `planHash`, `applySetupEdit`,
 * `verifyPlanIntent`, `proposeUndo`, `validateSetupPlan` and the presets. The hub module `setup`
 * (SET-2) stores plans and runs them. Browser-safe: no I/O, no clock unless none is given, and no
 * runtime import of the AI catalogue (so ai.ts may import this file without a cycle).
 */
import type { Id, Ts, UiErrorShape } from '../index.ts';
import type { CapabilityTier } from './ai.ts';
import { agentKindLabel } from './adapters.ts';
import { CI_PROVIDER_IDS, CI_PROVIDER_LABEL } from './ci.ts';
import type { CiProviderId } from './ci.ts';
import type { EnvironmentKind } from './landing.ts';
import { DEFAULT_PRODUCTION_APPROVAL_POLICY } from './rbac.ts';
import type { ApprovalPolicyAppliesTo, ApprovalPolicyScope } from './rbac.ts';
import type { StackCommand, StackProfile } from './stack.ts';
import { ulid } from './ids.ts';
import { canonicalJson } from './trust.ts';

export type SetupArea =
  | 'project'
  | 'ci'
  | 'landing'
  | 'environments'
  | 'policy'
  | 'budgets'
  | 'approvals'
  | 'people'
  | 'notify'
  | 'machine'
  | 'mission'
  | 'standards'
  | 'agents';

/** The order the plan card groups steps in. */
export const SETUP_AREAS: readonly SetupArea[] = ['project', 'ci', 'landing', 'environments', 'policy', 'approvals', 'budgets', 'standards', 'agents', 'people', 'notify', 'machine', 'mission'];

export const SETUP_AREA_LABEL: Record<SetupArea, string> = {
  project: 'Projects',
  ci: 'CI/CD',
  landing: 'Landing',
  environments: 'Environments',
  policy: 'Policy',
  approvals: 'Approvals',
  budgets: 'Budgets',
  standards: 'Standards',
  agents: 'Agents',
  people: 'People',
  notify: 'Notifications',
  machine: 'This computer',
  mission: 'First mission',
};

/**
 * The only capabilities a setup plan may name (ADR 0047 point 2, ADR 0050 point 6). In setup mode
 * the assistant is offered only these and the explain reads, so text in a repo cannot steer it
 * into other tools. The ones in SETUP_HANDOFF_CAPABILITIES are handoffs a person does: the
 * executor never runs them.
 */
export const SETUP_CAPABILITIES: readonly string[] = Object.freeze([
  'setup.detect',
  'setup.plan',
  'landing.configure',
  'environment.configure',
  'budget.set',
  'policy.preset.apply',
  'approval-policy.set',
  'team.create',
  'role.bind',
  'member.invite',
  'notify.channel.add',
  'ci.connect',
  'standards.adopt',
  'standards.pr',
  'tokens.import',
  'machine.pair',
  'agents.sign-in',
  'mission.start',
]);

/**
 * Steps a person does by clicking (consent, a credential, a download, starting the first mission).
 * A plan step with one of these carries a `handoff` and is never run as `agent:assistant`; the
 * plan-bound intent never covers it.
 */
export const SETUP_HANDOFF_CAPABILITIES: readonly string[] = Object.freeze(['ci.connect', 'tokens.import', 'machine.pair', 'agents.sign-in', 'mission.start']);

export function isSetupCapability(name: string): boolean {
  return SETUP_CAPABILITIES.includes(name);
}

export function isSetupHandoffCapability(name: string): boolean {
  return SETUP_HANDOFF_CAPABILITIES.includes(name);
}

/**
 * The tier of each setup capability before escalations (ADR 0047 point 2, ADR 0050 point 5). The
 * catalogue entries CAT-1 adds to ai.ts must carry the same tiers; a test holds them together.
 */
export const SETUP_CAPABILITY_TIER: Readonly<Record<string, CapabilityTier>> = Object.freeze({
  'setup.detect': 'read',
  'setup.plan': 'read',
  'landing.configure': 'elevated',
  'environment.configure': 'elevated',
  'budget.set': 'elevated',
  'policy.preset.apply': 'critical',
  'approval-policy.set': 'critical',
  'team.create': 'critical',
  'role.bind': 'critical',
  'member.invite': 'critical',
  'notify.channel.add': 'elevated',
  'ci.connect': 'critical',
  'standards.adopt': 'elevated',
  'standards.pr': 'elevated',
  'tokens.import': 'elevated',
  'machine.pair': 'critical',
  'agents.sign-in': 'operate',
  'mission.start': 'operate',
});

/** Read capabilities the assistant (and every page's "Ask why") may call while setting up. */
export const EXPLAIN_CAPABILITIES: readonly string[] = Object.freeze(['access.explain', 'policy.explain', 'pipeline.explain', 'cost.explain', 'standards.explain']);

/** A plan runs at most this many steps; a longer one is split. */
export const SETUP_MAX_STEPS = 40;
/** An approved plan runs paced at up to this many steps a second, outside the agent change limit. */
export const SETUP_STEPS_PER_SECOND = 2;

/**
 * 'ci-connection': the CI connection form in Settings (Jenkins, CircleCI and the other
 * token-based providers). 'start-mission': the Start mission form, filled in.
 */
export type SetupHandoffKind = 'github-app' | 'gitlab-oauth' | 'ci-connection' | 'figma-oauth' | 'upload' | 'download' | 'sign-in' | 'add-computer' | 'start-mission';

/** A button the person clicks; the plan resumes when it comes back. Never carries a secret. */
export interface SetupHandoff {
  kind: SetupHandoffKind;
  url: string;
  /** "Install the Orgus GitHub App on the repos you choose". */
  label: string;
}

export type SetupStepState = 'proposed' | 'skipped' | 'running' | 'done' | 'failed';

export interface SetupStep {
  /** Stable within the plan: 'landing.configure:prj_...'. */
  id: string;
  area: SetupArea;
  /** A name from SETUP_CAPABILITIES. */
  capability: string;
  /** Validated against the capability's input schema. */
  input: Record<string, unknown>;
  /** effectiveTier(capability, input). */
  tier: CapabilityTier;
  /** "Verify landings with pnpm test". */
  title: string;
  /** "package.json declares scripts.test; CI runs it on every pull request". */
  why: string;
  /** Detection evidence lines. */
  evidence: string[];
  /** Steps that must be done first. */
  dependsOn?: string[];
  undo?: { capability: string; input: Record<string, unknown> };
  handoff?: SetupHandoff;
  state: SetupStepState;
  result?: { auditId?: Id; error?: UiErrorShape; at: Ts };
}

export type SetupPlanStatus = 'draft' | 'approved' | 'applying' | 'applied' | 'partial' | 'failed' | 'undone';

export interface SetupPlanScope {
  orgId: Id;
  workspaceId?: Id;
  projectIds: Id[];
}

export interface SetupPlan {
  /** stp_<ulid> */
  id: Id;
  scope: SetupPlanScope;
  /** The person, or 'agent:assistant' on behalf of one. */
  createdBy: string;
  createdAt: Ts;
  /** The detection it came from. */
  basedOn: { profileHash: string; at: Ts };
  /** The one sentence the plan card leads with (R-ON-5). */
  summary: string;
  steps: SetupStep[];
  status: SetupPlanStatus;
  /** Bound to the exact steps and inputs: any edit changes the hash and needs approval again. */
  approval?: { by: string; at: Ts; planHash: string };
  /** For an undo plan: the plan it reverses. */
  undoOf?: Id;
  /** What the rules could not tell: one question each, answered through SetupInput.answers. */
  questions?: SetupQuestion[];
  /** Steps left out because the requester cannot do them, or (in an undo plan) have no undo. */
  withheld?: SetupWithheld[];
  /** Projects left for a next plan, so that no plan runs more than SETUP_MAX_STEPS. */
  deferredProjectIds?: Id[];
}

/** One question instead of a guess ("Which command runs the tests in web?"). */
export interface SetupQuestion {
  /** 'testCommand:prj_1'. */
  id: string;
  projectId?: Id;
  /** The answer field in SetupInput.answers, when a typed answer resolves it. */
  key?: keyof SetupAnswers;
  text: string;
}

export interface SetupWithheld {
  capability: string;
  title: string;
  /** "Needs a higher role: Critical changes are the owner's." */
  why: string;
}

/** A person's answers to a plan's questions, per project. */
export interface SetupAnswers {
  testCommand?: string;
}

export function newSetupPlanId(now?: number): Id {
  return `stp_${ulid(now)}`;
}

/**
 * The plan-bound intent: a person's approval of one plan hash lets 'agent:assistant' run each
 * listed step (critical ones included) on their behalf, and nothing else. It generalises the
 * per-call AiIntent of ADR 0041 with how 'plan'. The gate trusts none of these fields on their
 * own: it loads the plan by `planId`, recomputes its hash, and runs the step only when the hash
 * equals both `planHash` and the approval's, the approver is a person (never an agent), the step
 * is in the plan with exactly this capability and input, is not done yet (a step runs once), the
 * capability is in SETUP_CAPABILITIES and is not a handoff, and `by` still holds the grant at that
 * step's scope. A step in state 'running' still passes, so the executor can mark it before it
 * calls the gate: the hub claims each step atomically, so a replayed intent cannot run it twice.
 */
export interface SetupPlanIntent {
  how: 'plan';
  planId: Id;
  planHash: string;
  /** The step being run, which must be in the approved plan with the same capability and input. */
  planStep: string;
  capability: string;
  /** The approver. */
  by: string;
}

/** What `proposeSetup` reads. Same input, same plan. */
export interface SetupInput {
  scope: SetupPlanScope;
  /**
   * The person asking and their org role (the plan never grants more than they hold). `ceiling`
   * replaces the role's tier ceiling once RBAC v2 grants are known (never above it is honoured
   * by the gate anyway); `email` adds their own notification channel.
   */
  requester: { id: string; role: 'owner' | 'admin' | 'operator' | 'viewer'; ceiling?: CapabilityTier; email?: string };
  /** One profile per project. */
  profiles: Record<Id, StackProfile>;
  /** People in the org, and eligible production approvers per project. */
  orgSize: number;
  eligibleApprovers?: Record<Id, number>;
  /** What the provider says about each project (environments, protected branches, CODEOWNERS, teams). */
  providerFacts?: Record<Id, SetupProviderFacts>;
  /** Machines with a runtime installed and signed in, for agent kinds. */
  runtimes?: { kind: string; signedIn: boolean }[];
  /** Whether a code provider is connected yet (else the plan leads with the handoff). */
  codeConnected: boolean;
  /** Whether a machine is paired yet. */
  machinePaired: boolean;
  /** Whether the hub runs on the hosted server (webhooks) or locally (polling, a machine first). */
  hosted: boolean;
  /** Project names for titles ("Verify landings in web"); the id otherwise. */
  projectNames?: Record<Id, string>;
  /** CI providers that already have a connection (else a ci.connect handoff per detected provider). */
  connectedCi?: CiProviderId[];
  /** onboardScan's result per project and pack ('orgus/typescript-react'), the evidence for standards.adopt. */
  standardsScores?: Record<Id, Record<string, { score: number; violations: number }>>;
  /** Who to invite: the one field the person fills in (R-ON-8). */
  invite?: { emails?: string[]; domain?: string };
  /** Answers to an earlier plan's questions. */
  answers?: Record<Id, SetupAnswers>;
}

export interface SetupProviderFacts {
  defaultBranch?: string;
  environments?: { name: string; protected: boolean }[];
  protectedBranches?: string[];
  /** CODEOWNERS owners for the repo root. */
  owners?: string[];
  /** GitHub org teams or GitLab groups with access. */
  teams?: { slug: string; members: string[] }[];
}

/** A named starting point for the numbers in a plan (owner to confirm; settings, not code). */
export interface SetupPreset {
  id: 'default' | (string & {});
  /** Soft monthly org budget warning at this share. */
  budgetWarnShare: number;
  /** Production approvers: one, two when this many are eligible. */
  secondApproverFrom: number;
  /** Invitees join as this role in their workspace. */
  inviteRole: 'operator' | 'viewer';
  /** Missions from templates land through the queue after review when the project lands. */
  missionLand: 'after-review' | 'never';
  /** The soft monthly org budget, USD: a warning at budgetWarnShare, never a stop. */
  orgMonthlyBudgetUsd: number;
  /** The hard limit of each mission, USD: the mission stops there. */
  missionBudgetUsd: number;
}

export const DEFAULT_SETUP_PRESET: Readonly<SetupPreset> = Object.freeze({
  id: 'default',
  budgetWarnShare: 0.8,
  secondApproverFrom: 3,
  inviteRole: 'operator',
  missionLand: 'after-review',
  orgMonthlyBudgetUsd: 200,
  missionBudgetUsd: 25,
});

// ---------------------------------------------------------------------------
// HTTP bodies and state
// ---------------------------------------------------------------------------

/** POST /api/setup/plans */
export interface SetupPlanRequest {
  projectIds: Id[];
  workspaceId?: Id;
}

/** PATCH /api/setup/plans/:id: untick steps or change inputs; the hash changes. */
export interface SetupPlanEdit {
  skip?: string[];
  unskip?: string[];
  inputs?: Record<string, Record<string, unknown>>;
}

/** POST /api/setup/plans/:id/apply: names the hash the person saw. */
export interface SetupApplyRequest {
  planHash: string;
}

/** POST /api/setup/plans/:id/retry */
export interface SetupRetryRequest {
  stepId: string;
}

/** POST /api/setup/detect */
export interface SetupDetectRequest {
  projectId: Id;
}

/** state.ext.setup and GET /api/setup/plans */
export interface SetupState {
  plans: SetupPlan[];
  /** The plan being applied, if any. */
  activeId: Id | null;
}

// ---------------------------------------------------------------------------
// Step inputs: what each setup capability receives (the executor maps them onto module APIs)
// ---------------------------------------------------------------------------

/**
 * An undo input names the same capability with `restore: 'previous'` (the executor saved the value
 * it replaced) or `remove: true` (it deletes what the step created). Neither is ever proposed by
 * proposeSetup itself, only by proposeUndo.
 */
export interface SetupUndoMarker {
  restore?: 'previous';
  remove?: true;
}

/** landing.configure */
export interface SetupLandingInput extends SetupUndoMarker {
  projectId: Id;
  /** 'ci': the queue waits for these checks on the exact commit through the project's connection for `provider`. */
  verify?: { mode: 'command'; command: string } | { mode: 'ci'; provider: CiProviderId; requiredChecks: string[] };
  protectedBranches?: string[];
  /** What missions started from templates in this project do when done (ADR 0045). */
  missions?: { land: { when: 'after-review' } | null };
}

/** environment.configure */
export interface SetupEnvironmentInput extends SetupUndoMarker {
  projectId: Id;
  environments?: { name: string; kind: EnvironmentKind; autoDeployFrom: string | null; protected: boolean }[];
  /** 'observed': the CI deploys by itself and Orgus records it; 'command': nothing deploys until a deploy command is set. */
  deploy?: { mode: 'command' } | { mode: 'observed'; provider: CiProviderId };
}

/** policy.preset.apply: 'default' is the built-in policy plus allow rules; 'agent-kinds' sets which agents missions may use. */
export interface SetupPolicyPresetInput extends SetupUndoMarker {
  scope: { kind: 'org' } | { kind: 'project'; projectId: Id };
  preset: 'default' | 'agent-kinds';
  /** Exact commands agents may run without asking (no glob, no shell operator). */
  allow?: { tool: 'Bash'; pattern: string }[];
  allowedAgentKinds?: string[];
  defaultAgentKind?: string;
  allowCloudAgents?: boolean;
}

/** approval-policy.set (RBAC v2 point 8). */
export interface SetupApprovalPolicyInput extends SetupUndoMarker {
  scope: ApprovalPolicyScope;
  appliesTo?: ApprovalPolicyAppliesTo[];
  minApprovers?: number;
  raiseTo?: { approvers: number; whenEligibleAtLeast: number };
  eligible?: 'capability';
  forbidSelfApproval?: boolean;
  forbidAuthorApproval?: boolean;
  expiresAfterMinutes?: number;
}

/** budget.set: 'each-mission' is the default hard limit every mission gets. */
export interface SetupBudgetInput extends SetupUndoMarker {
  scope: { kind: 'org' } | { kind: 'each-mission' };
  period?: 'month' | 'total';
  /** USD. */
  limit?: number;
  softAt?: number;
  onLimit?: 'warn' | 'stop';
}

/** team.create: a team from the code provider's org teams or groups; members are provider logins. */
export interface SetupTeamInput extends SetupUndoMarker {
  name: string;
  source: 'provider';
  members?: string[];
}

/** role.bind: never 'owner'; the executor resolves the handle ('acme/web' team, 'alice' member) to a subject. */
export interface SetupRoleBindInput extends SetupUndoMarker {
  subject: { kind: 'team' | 'member'; handle: string };
  role: 'admin' | 'operator' | 'viewer';
  scope: { kind: 'project'; id: Id };
}

/** member.invite (ADR 0039's caps apply). */
export interface SetupInviteInput extends SetupUndoMarker {
  emails?: string[];
  domain?: string;
  role: 'operator' | 'viewer';
  workspaceId?: Id;
}

/** notify.channel.add: the requester's own email channel. */
export interface SetupNotifyChannelInput extends SetupUndoMarker {
  kind: 'email';
  name: string;
  address: string;
  memberId: string;
  minUrgency: 'high';
}

/** standards.adopt: pin packs to a project; `enforce` (critical) is never proposed. */
export interface SetupStandardsAdoptInput extends SetupUndoMarker {
  projectId: Id;
  packs?: string[];
  enforce?: boolean;
}

/** mission.start: the Start mission form, filled in. */
export interface SetupMissionInput {
  projectId: Id;
  template: string;
  values: Record<string, string>;
  land: { when: 'after-review' } | null;
}

// ---------------------------------------------------------------------------
// The planner
// ---------------------------------------------------------------------------

const ROLE_RANK: Record<SetupInput['requester']['role'], number> = { viewer: 0, operator: 1, admin: 2, owner: 3 };
const TIER_ORDER: readonly CapabilityTier[] = ['read', 'operate', 'elevated', 'critical'];
const tierRankOf = (t: CapabilityTier): number => TIER_ORDER.indexOf(t);
/** The v1 role ceilings (ROLE_CEILING in ai.ts), kept here so this file never imports ai.ts at run time. */
const SETUP_ROLE_CEILING: Record<SetupInput['requester']['role'], CapabilityTier> = { viewer: 'read', operator: 'operate', admin: 'elevated', owner: 'critical' };

/** The tier a step needs: its capability's tier, raised when the input loosens production, enforces, or restores an unknown value. */
export function setupStepTier(capability: string, input: Record<string, unknown> = {}): CapabilityTier {
  let tier: CapabilityTier = SETUP_CAPABILITY_TIER[capability] ?? 'critical';
  if (capability === 'environment.configure') {
    // Production by kind or by name, so relabelling 'production' as staging cannot dodge the check.
    const prod = (e: Record<string, unknown>) => e.kind === 'production' || (typeof e.name === 'string' && envKindOf(envName(e.name)) === 'production');
    const list = input.environments;
    const envs = Array.isArray(list) ? (list as unknown[]).map((e) => (e && typeof e === 'object' ? (e as Record<string, unknown>) : {})) : [];
    // Loosening production: unprotecting it, deploying to it automatically from a branch, or a list without a protected production.
    const loosens =
      envs.some((e) => prod(e) && (e.protected !== true || (e.autoDeployFrom !== undefined && e.autoDeployFrom !== null))) ||
      (list !== undefined && !envs.some((e) => prod(e) && e.protected === true));
    if (loosens || input.restore !== undefined || input.remove !== undefined) tier = 'critical';
  }
  if (capability === 'standards.adopt' && input.enforce === true) tier = 'critical';
  return tier;
}

export interface ProposeSetupOptions {
  /** The plan id (stp_<ulid>); a new one otherwise. */
  id?: Id;
  /** The clock; Date.now() otherwise. Same input, options and clock: the same plan. */
  now?: Ts;
  preset?: SetupPreset;
  /** Leave out the org-wide steps (connections, budgets, agents, people, this computer, the first mission): for the next plan of a split. */
  projectsOnly?: boolean;
}

/** A step before its state is set. */
type Draft = Omit<SetupStep, 'state' | 'tier'> & { state?: SetupStepState; projectId?: Id };

const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f\u061c\u180e\u200b-\u200f\u2028-\u202e\u2060-\u2069\ufeff]/g;

/** Repo text is untrusted data: no control or direction characters, nothing shaped like a secret, one line, bounded. */
export function cleanSetupText(raw: unknown, max = 200): string {
  let s = String(raw ?? '')
    .replace(CONTROL_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  for (const r of SECRET_VALUES) s = s.replace(new RegExp(r.source, `${r.flags.replace('g', '')}g`), '[redacted]');
  return s.length > max ? `${s.slice(0, Math.max(0, max - 3))}...` : s;
}

/** A command a plan may propose: one line, printable, bounded, carrying no secret. Null when it is anything else. */
function plainCommand(raw: string): string | null {
  if (typeof raw !== 'string' || new RegExp(CONTROL_CHARS.source).test(raw)) return null;
  const s = raw.trim();
  return s && s.length <= 200 && !findSetupSecret(s) ? s : null;
}

/**
 * Safe to allow without asking: no shell operator, no glob, no quote or escape, and no flag that
 * hands the rest of the line to an interpreter ('sh -c', 'node -e', 'cmd /c', '--eval', '-exec').
 */
export function allowRuleSafe(cmd: string): boolean {
  return /^[A-Za-z0-9 ._:/=@+,-]+$/.test(cmd) && !/(^|\s)-{1,2}[A-Za-z-]*(exec|eval|command)\b/i.test(cmd) && !/(^|\s)(-c|-e|\/c|\/k)(\s|=|$)/i.test(cmd);
}

const CI_HANDOFF: Partial<Record<CiProviderId, SetupHandoffKind>> = { 'github-actions': 'github-app', 'gitlab-ci': 'gitlab-oauth' };
const CI_ORDER = CI_PROVIDER_IDS;
const CI_NAME = CI_PROVIDER_LABEL;
const agentName = (kind: string): string => agentKindLabel(kind);

/** Frameworks that draw a user interface: the project gets design-system and accessibility standards. */
const UI_FRAMEWORKS = new Set(['react', 'next', 'nextjs', 'remix', 'gatsby', 'vue', 'nuxt', 'svelte', 'sveltekit', 'angular', 'solid', 'preact', 'astro', 'qwik', 'ember', 'react-native', 'expo', 'flutter', 'swiftui', 'jetpack-compose', 'compose', 'blazor', 'electron', 'tauri']);
const REACT_LIKE = new Set(['react', 'next', 'nextjs', 'remix', 'gatsby', 'preact', 'react-native', 'expo', 'vue', 'nuxt', 'svelte', 'sveltekit', 'angular', 'solid', 'astro', 'qwik']);

/**
 * The orgus/* standards packs for a stack (ADR 0050 point 1). STD-1 ships their content; the
 * names are fixed here so a plan can name them before it lands.
 */
export function standardsPacksFor(profile: Pick<StackProfile, 'languages' | 'frameworks'>): string[] {
  const langs = new Set(profile.languages.map((l) => l.toLowerCase()));
  const fws = new Set(profile.frameworks.map((f) => f.toLowerCase()));
  const has = (set: Set<string>, ...names: string[]) => names.some((n) => set.has(n));
  const out: string[] = [];
  const js = has(langs, 'typescript', 'javascript');
  if (js && [...fws].some((f) => REACT_LIKE.has(f))) out.push('orgus/typescript-react');
  else if (js) out.push('orgus/node-service');
  if (langs.has('python')) out.push('orgus/python-service');
  if (langs.has('go')) out.push('orgus/go-service');
  if (has(langs, 'java', 'kotlin') && has(fws, 'spring', 'spring-boot')) out.push('orgus/java-spring');
  if (has(langs, 'csharp', 'fsharp', 'dotnet')) out.push('orgus/dotnet');
  if (langs.has('rust')) out.push('orgus/rust');
  if (langs.has('swift') || has(fws, 'swiftui', 'ios')) out.push('orgus/mobile-ios');
  if (has(fws, 'android', 'jetpack-compose')) out.push('orgus/mobile-android');
  out.push('orgus/security-baseline', 'orgus/docs', 'orgus/git-release');
  if ([...fws].some((f) => UI_FRAMEWORKS.has(f))) out.push('orgus/design-system', 'orgus/accessibility');
  return out;
}

/** Environment kind from a provider's environment name, word by word ('eu-production', 'pre-prod', 'review-app'). */
function envKindOf(name: string): EnvironmentKind {
  const words = name.split('-');
  const any = (re: RegExp) => words.some((w) => re.test(w));
  if (any(/^(preprod|stag(e|ing)?|uat|qa|test(ing)?|sandbox)$/) || /(^|-)pre-prod(-|$)/.test(name)) return 'staging';
  if (any(/^(prod|production|prd|live)$/)) return 'production';
  if (any(/^(preview|review|dev|development|pr|feature)$/)) return 'preview';
  return 'staging';
}

function envName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function uniq<T>(xs: Iterable<T>): T[] {
  return [...new Set(xs)];
}

function listText(xs: string[], max = 3): string {
  const shown = xs.slice(0, max);
  const more = xs.length - shown.length;
  const head = more > 0 ? [...shown, `${more} more`] : shown;
  if (head.length <= 1) return head.join('');
  return `${head.slice(0, -1).join(', ')} and ${head[head.length - 1]}`;
}

const EMAIL = /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const DOMAIN = /^[a-z0-9-]{1,63}(\.[a-z0-9-]{1,63})+$/;
const HANDLE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}(\/[A-Za-z0-9][A-Za-z0-9._-]{0,99})?$/;
const urlPart = encodeURIComponent;
/** Mail domains anyone can get an address at: never a domain invite (ADR 0039's join rules refuse them too). */
const PUBLIC_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'proton.me', 'protonmail.com', 'pm.me', 'gmx.com', 'gmx.net', 'mail.com', 'yandex.com', 'yandex.ru', 'zoho.com', 'qq.com', '163.com', 'fastmail.com', 'hey.com', 'tutanota.com',
]);

/**
 * Propose a setup plan (ADR 0047 point 1): pure and deterministic. Same input and options, the
 * same plan and hash. Every step names a setup capability, carries its tier, a title, a why and
 * the detection evidence; steps above the requester's ceiling are withheld, never proposed; no
 * step carries a secret; repo text appears only cleaned, as data.
 */
export function proposeSetup(input: SetupInput, opts: ProposeSetupOptions = {}): SetupPlan {
  const now = opts.now ?? Date.now();
  const preset = opts.preset ?? DEFAULT_SETUP_PRESET;
  const id = opts.id ?? newSetupPlanId(now);
  const projectIds = uniq(input.scope.projectIds);
  const name = (pid: Id) => cleanSetupText(input.projectNames?.[pid] ?? pid, 60);
  const questions: SetupQuestion[] = [];
  const org: Draft[] = [];
  const perProject = new Map<Id, Draft[]>();
  const connected = new Set(input.connectedCi ?? []);
  const facts = (pid: Id) => input.providerFacts?.[pid];
  const landWhen = preset.missionLand === 'after-review' ? ({ when: 'after-review' } as const) : null;

  // CI and code connections (org-wide handoffs).
  const detections = (pid: Id) => (input.profiles[pid]?.ci ?? []).filter((d) => CI_ORDER.includes(d.provider));
  const ciProviders = CI_ORDER.filter((p) => !connected.has(p) && projectIds.some((pid) => detections(pid).some((d) => d.provider === p)));
  const ciStepId = (p: CiProviderId) => `ci.connect:${p}`;
  if (!opts.projectsOnly) {
    const anyProfile = projectIds.some((pid) => input.profiles[pid]);
    if (!input.codeConnected && !anyProfile && input.hosted) {
      org.push({
        id: 'ci.connect:code',
        area: 'ci',
        capability: 'ci.connect',
        input: { purpose: 'code' },
        title: 'Connect your code',
        why: 'Orgus reads your repos, their CI runs and their environments through GitHub or GitLab. You choose the repos; Orgus never sees your password.',
        evidence: ['No repo is connected yet.'],
        handoff: { kind: 'github-app', url: '/settings/ci-connections?purpose=code', label: 'Connect GitHub or GitLab' },
      });
    }
    for (const p of ciProviders) {
      const files = uniq(projectIds.flatMap((pid) => detections(pid).filter((d) => d.provider === p).flatMap((d) => d.files))).map((f) => cleanSetupText(f, 120));
      const kind = CI_HANDOFF[p] ?? 'ci-connection';
      const label = kind === 'github-app' ? 'Install the Orgus GitHub App on the repos you choose' : kind === 'gitlab-oauth' ? 'Allow Orgus to read your GitLab projects' : `Connect ${CI_NAME[p]} in Settings`;
      org.push({
        id: ciStepId(p),
        area: 'ci',
        capability: 'ci.connect',
        input: { provider: p },
        title: `Connect ${CI_NAME[p]}`,
        why: `Your repos run ${CI_NAME[p]}. Connecting it shows real runs on Pipelines and lets the landing queue wait for its checks. You grant access yourself; no key passes through the assistant.`,
        evidence: files.slice(0, 5),
        handoff: { kind, url: `/settings/ci-connections?provider=${urlPart(p)}`, label },
      });
    }
  }

  for (const pid of projectIds) {
    const steps: Draft[] = [];
    perProject.set(pid, steps);
    const profile = input.profiles[pid];
    if (!profile) {
      questions.push({ id: `profile:${pid}`, projectId: pid, text: `Orgus has not read ${name(pid)} yet. Connect your code or this computer, then plan again.` });
      continue;
    }
    const f = facts(pid);
    const defaultBranch = cleanSetupText(f?.defaultBranch || 'main', 100);
    const answered = plainCommand(input.answers?.[pid]?.testCommand ?? '');
    const test: StackCommand | undefined = answered ? { command: answered, confidence: 'declared', evidence: 'You answered the question' } : profile.commands.test;
    const testCmd = test ? plainCommand(test.command) : null;
    const testEvidence = test ? cleanSetupText(test.evidence, 160) : '';
    const prCi = detections(pid).find((d) => (d.pullRequestJobs ?? []).length > 0);
    const ciDeps = (p: CiProviderId) => (ciProviders.includes(p) && !opts.projectsOnly ? [ciStepId(p)] : []);

    // Landing.
    const landingId = `landing.configure:${pid}`;
    const protectedBranches = uniq((f?.protectedBranches ?? []).map((b) => cleanSetupText(b, 100)).filter(Boolean)).slice(0, 10);
    let landing: Draft | null = null;
    if (prCi) {
      const checks = uniq((prCi.pullRequestJobs ?? []).map((j) => cleanSetupText(j, 100)).filter(Boolean)).slice(0, 10);
      const verify = { mode: 'ci' as const, provider: prCi.provider, requiredChecks: checks };
      landing = {
        id: landingId,
        area: 'landing',
        capability: 'landing.configure',
        input: { projectId: pid, verify, protectedBranches: protectedBranches.length ? protectedBranches : [defaultBranch], missions: { land: landWhen } } satisfies SetupLandingInput,
        title: `Land changes in ${name(pid)} when ${CI_NAME[prCi.provider]} passes`,
        why: `${CI_NAME[prCi.provider]} runs ${listText(checks)} on every pull request, so the landing queue waits for those checks on the exact commit before anything reaches ${defaultBranch}.`,
        evidence: prCi.files.slice(0, 5).map((x) => cleanSetupText(x, 120)),
        dependsOn: ciDeps(prCi.provider),
        undo: { capability: 'landing.configure', input: { projectId: pid, restore: 'previous' } },
        projectId: pid,
      };
    } else if (testCmd) {
      landing = {
        id: landingId,
        area: 'landing',
        capability: 'landing.configure',
        input: { projectId: pid, verify: { mode: 'command', command: testCmd }, protectedBranches: protectedBranches.length ? protectedBranches : [defaultBranch], missions: { land: landWhen } } satisfies SetupLandingInput,
        title: `Verify landings in ${name(pid)} with ${cleanSetupText(testCmd, 80)}`,
        why: `${testEvidence || 'The project'} runs the tests with ${cleanSetupText(testCmd, 80)}; the landing queue runs it on the rebased change and lands only when it passes. No CI runs on pull requests here, so the queue verifies on one of your machines.`,
        evidence: [testEvidence].filter(Boolean),
        undo: { capability: 'landing.configure', input: { projectId: pid, restore: 'previous' } },
        projectId: pid,
      };
    } else {
      questions.push({ id: `testCommand:${pid}`, projectId: pid, key: 'testCommand', text: `Which command runs the tests in ${name(pid)}?` });
    }
    if (landing) {
      if (testCmd && !prCi && !allowRuleSafe(testCmd)) landing.evidence.push('The test command uses shell operators or quotes, so no allow rule is proposed: agents ask before running it.');
      steps.push(landing);
    }

    // Environments.
    const providerEnvs = (f?.environments ?? []).map((e) => ({ name: envName(cleanSetupText(e.name, 80)), protected: e.protected === true })).filter((e) => e.name);
    const seen = new Set<string>();
    const unique = providerEnvs
      .filter((e) => (seen.has(e.name) ? false : (seen.add(e.name), true)))
      .map((e) => ({ name: e.name, kind: envKindOf(e.name), autoDeployFrom: null as string | null, protected: e.protected }));
    // At most 10, never by dropping a production environment: those are kept first, the rest fill up, in the provider's order.
    const keep = new Set([...unique.filter((e) => e.kind === 'production'), ...unique.filter((e) => e.kind !== 'production')].slice(0, 10));
    let envs = unique.filter((e) => keep.has(e));
    if (!envs.length)
      envs = [
        { name: 'preview', kind: 'preview', autoDeployFrom: null, protected: false },
        { name: 'staging', kind: 'staging', autoDeployFrom: defaultBranch, protected: false },
        { name: 'production', kind: 'production', autoDeployFrom: null, protected: true },
      ];
    else {
      const firstStaging = envs.find((e) => e.kind === 'staging');
      if (firstStaging) firstStaging.autoDeployFrom = defaultBranch;
      if (!envs.some((e) => e.kind === 'production')) envs.push({ name: 'production', kind: 'production', autoDeployFrom: null, protected: true });
    }
    for (const e of envs) if (e.kind === 'production') e.protected = true;
    const deployCi = providerEnvs.length ? detections(pid)[0] : undefined;
    const hints = profile.deployHints.slice(0, 3).map((h) => `${cleanSetupText(h.target, 40)} (${cleanSetupText(h.evidence, 80)})`);
    const envId = `environment.configure:${pid}`;
    steps.push({
      id: envId,
      area: 'environments',
      capability: 'environment.configure',
      input: { projectId: pid, environments: envs, deploy: deployCi ? { mode: 'observed', provider: deployCi.provider } : { mode: 'command' } } satisfies SetupEnvironmentInput,
      title: `Set up ${listText(envs.map((e) => e.name), 4)} for ${name(pid)}`,
      why: providerEnvs.length
        ? `Your provider already has these environments; Orgus records the deploys ${deployCi ? CI_NAME[deployCi.provider] : 'it'} makes and keeps production protected.`
        : `Preview deploys nothing by itself, staging follows ${defaultBranch}, and production is protected. Nothing deploys until you set a deploy command.`,
      evidence: [providerEnvs.length ? `Provider environments: ${listText(providerEnvs.map((e) => e.name), 5)}` : 'No environments found at the provider.', ...hints.map((h) => `Deploy hint: ${h}`)],
      dependsOn: deployCi ? ciDeps(deployCi.provider) : [],
      undo: { capability: 'environment.configure', input: { projectId: pid, restore: 'previous' } },
      projectId: pid,
    });

    // Policy: an allow rule for the verify command, so the queue never waits on a question nobody answers.
    if (testCmd && allowRuleSafe(testCmd)) {
      steps.push({
        id: `policy.preset.apply:${pid}`,
        area: 'policy',
        capability: 'policy.preset.apply',
        input: { scope: { kind: 'project', projectId: pid }, preset: 'default', allow: [{ tool: 'Bash', pattern: testCmd }] } satisfies SetupPolicyPresetInput,
        title: `Let agents run ${cleanSetupText(testCmd, 80)} in ${name(pid)} without asking`,
        why: 'The built-in policy denies destructive commands and asks before network use and writes outside the session folder. Allowing the exact test command keeps agents and the landing queue from waiting on a question nobody is there to answer.',
        evidence: [testEvidence].filter(Boolean),
        undo: { capability: 'policy.preset.apply', input: { scope: { kind: 'project', projectId: pid }, preset: 'default', restore: 'previous' } },
        projectId: pid,
      });
    }

    // Approvals for production.
    const rawEligible = Number(input.eligibleApprovers?.[pid] ?? input.orgSize);
    const eligible = Number.isFinite(rawEligible) ? Math.max(0, Math.min(10_000, Math.floor(rawEligible))) : 0;
    const minApprovers = eligible >= preset.secondApproverFrom ? 2 : 1;
    for (const env of envs.filter((e) => e.kind === 'production')) {
      const scope: ApprovalPolicyScope = { kind: 'environment', projectId: pid, env: env.name };
      const solo = eligible < 2 ? ' Until a second person can approve, the owner approves their own deploys with a typed reason, recorded as solo-owner.' : '';
      steps.push({
        id: `approval-policy.set:${pid}:${env.name}`,
        area: 'approvals',
        capability: 'approval-policy.set',
        input: {
          scope,
          appliesTo: [...DEFAULT_PRODUCTION_APPROVAL_POLICY.appliesTo],
          minApprovers,
          raiseTo: { approvers: 2, whenEligibleAtLeast: preset.secondApproverFrom },
          eligible: 'capability',
          forbidSelfApproval: true,
          forbidAuthorApproval: true,
          expiresAfterMinutes: DEFAULT_PRODUCTION_APPROVAL_POLICY.expiresAfterMinutes,
        } satisfies SetupApprovalPolicyInput,
        title: minApprovers === 2 ? `Production deploys in ${name(pid)} need two approvers` : `Production deploys in ${name(pid)} need an approver who did not make the change`,
        why: `${eligible === 1 ? 'One person' : `${eligible} people`} can approve production in ${name(pid)}. The person who asked and the author of the change never approve their own deploy; from ${preset.secondApproverFrom} eligible approvers, two must agree.${solo}`,
        evidence: [`${eligible} eligible approvers`],
        dependsOn: [envId],
        undo: { capability: 'approval-policy.set', input: { scope, restore: 'previous' } },
        projectId: pid,
      });
    }

    // Standards.
    const packs = standardsPacksFor(profile);
    const scores = input.standardsScores?.[pid] ?? {};
    const scoreLines = packs
      .filter((p) => scores[p])
      .map((p) => `Your repo already scores ${Math.round(scores[p]!.score)} on ${p}; ${scores[p]!.violations} violations would be tracked as debt, not blocked.`);
    const adoptId = `standards.adopt:${pid}`;
    steps.push({
      id: adoptId,
      area: 'standards',
      capability: 'standards.adopt',
      input: { projectId: pid, packs, enforce: false } satisfies SetupStandardsAdoptInput,
      title: `Adopt ${packs[0]}${packs.length > 1 ? ` and ${packs.length - 1} more` : ''} for ${name(pid)}`,
      why: 'Every agent session in this project starts with these standards, and edits are checked against them. Existing violations are tracked as debt, not blocked.',
      evidence: [
        `Languages: ${listText(profile.languages.map((l) => cleanSetupText(l, 30)), 5) || 'none found'}`,
        ...(profile.frameworks.length ? [`Frameworks: ${listText(profile.frameworks.map((x) => cleanSetupText(x, 30)), 5)}`] : []),
        ...scoreLines,
      ],
      undo: { capability: 'standards.adopt', input: { projectId: pid, restore: 'previous' } },
      projectId: pid,
    });
    if (input.codeConnected && f) {
      steps.push({
        id: `standards.pr:${pid}`,
        area: 'standards',
        capability: 'standards.pr',
        input: { projectId: pid },
        title: `Open a pull request with AGENTS.md and the standards files for ${name(pid)}`,
        why: 'Agents that read repo files follow the same standards. Orgus never writes into your checkout: the pull request is yours to review and merge.',
        evidence: [],
        dependsOn: [adoptId],
        projectId: pid,
      });
    }

    // People: CODEOWNERS owners administer their project.
    const teamsHere = new Set((f?.teams ?? []).map((t) => cleanSetupText(t.slug, 200)));
    // '@alice', 'alice' and 'Alice' are one owner (one step id each).
    const ownerKeys = new Set<string>();
    const owners = (f?.owners ?? [])
      .map((o) => cleanSetupText(o, 200))
      .map((o) => (o.startsWith('@') ? o.slice(1) : o))
      .filter((o) => (HANDLE.test(o) || EMAIL.test(o)) && !ownerKeys.has(o.toLowerCase()) && !!ownerKeys.add(o.toLowerCase()))
      .slice(0, 5);
    for (const owner of owners) {
      const isTeam = owner.includes('/') && !owner.includes('@');
      const teamSlug = isTeam ? owner.split('/')[1]! : '';
      const teamStep = isTeam && (teamsHere.has(owner) || teamsHere.has(teamSlug)) && !opts.projectsOnly ? [`team.create:${teamsHere.has(owner) ? owner : teamSlug}`] : [];
      steps.push({
        id: `role.bind:${pid}:${owner}`,
        area: 'people',
        capability: 'role.bind',
        input: { subject: { kind: isTeam ? 'team' : 'member', handle: owner }, role: 'admin', scope: { kind: 'project', id: pid } } satisfies SetupRoleBindInput,
        title: `Make ${isTeam ? 'the team ' : ''}${owner} admins of ${name(pid)}`,
        why: `CODEOWNERS names ${owner} as the owner of ${name(pid)}. Project admins configure its landing, environments and standards, and nothing outside it.`,
        evidence: ['CODEOWNERS'],
        dependsOn: teamStep,
        undo: { capability: 'role.bind', input: { subject: { kind: isTeam ? 'team' : 'member', handle: owner }, role: 'admin', scope: { kind: 'project', id: pid }, remove: true } },
        projectId: pid,
      });
    }
  }

  // Org-wide steps.
  if (!opts.projectsOnly) {
    org.push(
      {
        id: 'budget.set:org',
        area: 'budgets',
        capability: 'budget.set',
        input: { scope: { kind: 'org' }, period: 'month', limit: preset.orgMonthlyBudgetUsd, softAt: preset.budgetWarnShare, onLimit: 'warn' } satisfies SetupBudgetInput,
        title: `Warn at ${Math.round(preset.budgetWarnShare * 100)} % of a ${preset.orgMonthlyBudgetUsd} USD monthly budget`,
        why: 'A soft monthly budget for the whole org: you hear about spend before it surprises you, and nothing stops.',
        evidence: [],
        undo: { capability: 'budget.set', input: { scope: { kind: 'org' }, restore: 'previous' } },
      },
      {
        id: 'budget.set:each-mission',
        area: 'budgets',
        capability: 'budget.set',
        input: { scope: { kind: 'each-mission' }, period: 'total', limit: preset.missionBudgetUsd, onLimit: 'stop' } satisfies SetupBudgetInput,
        title: `Stop any mission at ${preset.missionBudgetUsd} USD`,
        why: 'A hard limit per mission, so one runaway agent cannot spend the month.',
        evidence: [],
        undo: { capability: 'budget.set', input: { scope: { kind: 'each-mission' }, restore: 'previous' } },
      },
    );

    // Agents from the runtimes found.
    const byKind = new Map<string, boolean>();
    for (const r of input.runtimes ?? []) {
      if (typeof r?.kind !== 'string' || !/^[a-z][a-z0-9-]{1,39}$/.test(r.kind)) continue;
      byKind.set(r.kind, (byKind.get(r.kind) ?? false) || r.signedIn === true);
    }
    const kinds = [...byKind.keys()].sort();
    const signedIn = kinds.filter((k) => byKind.get(k));
    if (signedIn.length) {
      const def = signedIn.includes('claude-code') ? 'claude-code' : signedIn[0]!;
      org.push({
        id: 'policy.preset.apply:agent-kinds',
        area: 'agents',
        capability: 'policy.preset.apply',
        input: { scope: { kind: 'org' }, preset: 'agent-kinds', allowedAgentKinds: signedIn, defaultAgentKind: def, allowCloudAgents: false } satisfies SetupPolicyPresetInput,
        title: `Let missions use ${listText(signedIn.map(agentName), 4)}`,
        why: `These agents are installed and signed in on your computers; missions start with ${agentName(def)} unless you pick another. Cloud agents stay off, because they send the repository to a vendor: an owner turns them on.`,
        evidence: signedIn.map((k) => `${agentName(k)}: signed in`),
        undo: { capability: 'policy.preset.apply', input: { scope: { kind: 'org' }, preset: 'agent-kinds', restore: 'previous' } },
      });
    }
    for (const k of kinds.filter((x) => !byKind.get(x)).slice(0, 5)) {
      org.push({
        id: `agents.sign-in:${k}`,
        area: 'agents',
        capability: 'agents.sign-in',
        input: { kind: k },
        title: `Sign in to ${agentName(k)}`,
        why: `${agentName(k)} is installed but not signed in, so missions cannot use it yet. You sign in on your computer; Orgus never sees the credentials.`,
        evidence: [`${agentName(k)}: installed, not signed in`],
        handoff: { kind: 'sign-in', url: `/settings/agent-providers?kind=${urlPart(k)}`, label: `Sign in to ${agentName(k)} on your computer` },
      });
    }
    if (!kinds.length && input.machinePaired) questions.push({ id: 'agents', text: 'No coding agent is installed on your computers. Install Claude Code or another agent, sign in, and plan again.' });

    // Design tokens: optional, so proposed unticked.
    if (projectIds.some((pid) => (input.profiles[pid]?.frameworks ?? []).some((x) => UI_FRAMEWORKS.has(x.toLowerCase())))) {
      org.push({
        id: 'tokens.import',
        area: 'standards',
        capability: 'tokens.import',
        input: { sources: ['tokens-studio', 'dtcg-file'] },
        title: 'Import your design tokens',
        why: 'Your tokens become the design rules agents follow in every UI change. Optional: tick it when you have a Tokens Studio or W3C tokens file. Importing from Figma needs a Figma Enterprise plan.',
        evidence: ['A UI framework was found'],
        handoff: { kind: 'upload', url: '/settings/standards?tab=tokens&import=file', label: 'Upload a design tokens file' },
        state: 'skipped',
      });
    }

    // People.
    const emails = uniq((input.invite?.emails ?? []).map((e) => String(e).trim().toLowerCase()).filter((e) => EMAIL.test(e))).slice(0, 50);
    const rawDomain = String(input.invite?.domain ?? '').trim().toLowerCase().replace(/^@/, '');
    const domain = PUBLIC_MAIL_DOMAINS.has(rawDomain) ? '' : rawDomain;
    if (rawDomain && !domain) questions.push({ id: 'invite.domain', text: `Anyone can get an address at ${cleanSetupText(rawDomain, 60)}, so a plan never lets everyone there join. Invite those people by email.` });
    if (emails.length || DOMAIN.test(domain)) {
      const inv: SetupInviteInput = { role: preset.inviteRole, ...(emails.length ? { emails } : {}), ...(DOMAIN.test(domain) ? { domain } : {}), ...(input.scope.workspaceId ? { workspaceId: input.scope.workspaceId } : {}) };
      const who = [emails.length ? `${emails.length === 1 ? 'one person' : `${emails.length} people`}` : '', DOMAIN.test(domain) ? `anyone at ${domain}` : ''].filter(Boolean).join(' and ');
      org.push({
        id: 'member.invite',
        area: 'people',
        capability: 'member.invite',
        input: inv as unknown as Record<string, unknown>,
        title: `Invite ${who} as ${preset.inviteRole}s`,
        why: `Invitees join as ${preset.inviteRole}s${input.scope.workspaceId ? ' in this workspace' : ''}: they start sessions and missions, and cannot change policy, roles or budgets. You stay owner.`,
        evidence: [],
        undo: { capability: 'member.invite', input: { ...inv, remove: true } },
      });
    }
    const teams = new Map<string, string[]>();
    for (const pid of projectIds)
      for (const t of facts(pid)?.teams ?? []) {
        const slug = cleanSetupText(t.slug, 200);
        if (!HANDLE.test(slug) || (teams.size >= 10 && !teams.has(slug))) continue;
        teams.set(slug, uniq([...(teams.get(slug) ?? []), ...(t.members ?? []).map((m) => cleanSetupText(m, 100)).filter((m) => HANDLE.test(m))]).slice(0, 200));
      }
    for (const [slug, members] of teams) {
      org.push({
        id: `team.create:${slug}`,
        area: 'people',
        capability: 'team.create',
        input: { name: slug, source: 'provider', members } satisfies SetupTeamInput,
        title: `Create the team ${slug}`,
        why: `${slug} has access to your repos at the code provider; the same people become a team in Orgus, so roles and approvals can name them.`,
        evidence: [`${members.length} members at the provider`],
        undo: { capability: 'team.create', input: { name: slug, source: 'provider', remove: true } },
      });
    }

    // Notifications for the requester.
    const email = String(input.requester.email ?? '').trim().toLowerCase();
    if (EMAIL.test(email)) {
      org.push({
        id: 'notify.channel.add:me',
        area: 'notify',
        capability: 'notify.channel.add',
        input: { kind: 'email', name: 'My email', address: email, memberId: input.requester.id, minUrgency: 'high' } satisfies SetupNotifyChannelInput,
        title: `Email ${email} when an agent needs you`,
        why: 'Approvals, budget stops and failed landings reach you when you are not in the console.',
        evidence: [],
        undo: { capability: 'notify.channel.add', input: { kind: 'email', address: email, memberId: input.requester.id, remove: true } },
      });
    }

    // This computer.
    if (!input.machinePaired) {
      org.push({
        id: 'machine.pair',
        area: 'machine',
        capability: 'machine.pair',
        input: {},
        title: 'Add this computer',
        why: input.hosted
          ? 'Agents run on your own computers. Download the Orgus client, run it, and approve "Add this computer" here. It needs no admin rights.'
          : 'This hub reads your repos and runs agents through a connected computer. Download the Orgus client, run it, and approve "Add this computer" here.',
        evidence: ['No computer is connected yet.'],
        handoff: { kind: 'add-computer', url: '/machines/add', label: 'Download Orgus and add this computer' },
      });
    }
  }

  // Withhold what the requester cannot do (handoffs are the person's own clicks, checked when they act).
  const ceiling = input.requester.ceiling ?? SETUP_ROLE_CEILING[input.requester.role] ?? 'read';
  const withheld: (SetupWithheld & { projectId?: Id })[] = [];
  const out = new Set<string>();
  const all = [...org, ...[...perProject.values()].flat()];
  for (const d of all) {
    if (isSetupHandoffCapability(d.capability)) continue;
    const tier = setupStepTier(d.capability, d.input);
    const role = d.capability === 'role.bind' ? (d.input as unknown as SetupRoleBindInput).role : null;
    if (tierRankOf(tier) > tierRankOf(ceiling)) {
      out.add(d.id);
      withheld.push({ projectId: d.projectId, capability: d.capability, title: d.title, why: tier === 'critical' ? 'Needs the owner: this is a critical change.' : tier === 'elevated' ? 'Needs an admin: this change widens what runs unattended.' : 'Needs an operator.' });
    } else if (role && ROLE_RANK[role] > ROLE_RANK[input.requester.role]) {
      out.add(d.id);
      withheld.push({ projectId: d.projectId, capability: d.capability, title: d.title, why: 'A plan never grants more than you hold.' });
    }
  }
  for (let changed = true; changed; ) {
    changed = false;
    for (const d of all)
      if (!out.has(d.id) && (d.dependsOn ?? []).some((x) => out.has(x))) {
        out.add(d.id);
        changed = true;
        withheld.push({ projectId: d.projectId, capability: d.capability, title: d.title, why: 'Waits on a step you cannot do.' });
      }
  }

  // Fit in SETUP_MAX_STEPS: org steps, then whole projects in order, one slot kept for the first mission.
  const kept = (xs: Draft[]) => xs.filter((d) => !out.has(d.id));
  const steps: Draft[] = kept(org);
  const reserve = opts.projectsOnly ? 0 : 1;
  const deferred: Id[] = [];
  const included: Id[] = [];
  for (const pid of projectIds) {
    const xs = kept(perProject.get(pid) ?? []);
    if (deferred.length || steps.length + xs.length > SETUP_MAX_STEPS - reserve) {
      deferred.push(pid);
      continue;
    }
    steps.push(...xs);
    included.push(pid);
  }

  // The first mission, from a template, with the detected test command filled in (R-ON-7).
  if (!opts.projectsOnly) {
    const pid = included.find((p) => input.profiles[p]);
    if (pid) {
      const landingHere = steps.find((s) => s.id === `landing.configure:${pid}`);
      const cmd = (landingHere?.input as unknown as SetupLandingInput | undefined)?.verify;
      const command = cmd?.mode === 'command' ? cmd.command : plainCommand(input.answers?.[pid]?.testCommand ?? input.profiles[pid]?.commands.test?.command ?? '');
      steps.push({
        id: 'mission.start',
        area: 'mission',
        capability: 'mission.start',
        input: { projectId: pid, template: 'fix-failing-tests', values: command ? { command } : {}, land: landingHere ? landWhen : null } satisfies SetupMissionInput,
        title: `Start your first mission in ${name(pid)}`,
        why: landingHere && landWhen
          ? 'Fix failing tests, from a template with your test command filled in. When you have reviewed the result, it lands through the queue.'
          : 'Fix failing tests, from a template with your test command filled in.',
        evidence: [],
        dependsOn: ['machine.pair', `landing.configure:${pid}`],
        handoff: { kind: 'start-mission', url: `/getting-started?step=mission&template=fix-failing-tests&project=${urlPart(pid)}`, label: 'Start your first mission' },
      });
    }
  }

  // Order by area, keep only dependencies inside the plan, and set states and tiers.
  const ids = new Set(steps.map((s) => s.id));
  const rank = (a: SetupArea) => SETUP_AREAS.indexOf(a);
  const ordered = steps
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rank(a.s.area) - rank(b.s.area) || a.i - b.i)
    .map(({ s }): SetupStep => {
      const { projectId: _p, state, dependsOn, ...rest } = s;
      const deps = (dependsOn ?? []).filter((x) => ids.has(x));
      return { ...rest, ...(deps.length ? { dependsOn: deps } : {}), tier: setupStepTier(s.capability, s.input), state: state ?? 'proposed' };
    });
  // A dependency is always earlier: move a step after its dependencies when areas disagree.
  const placed: SetupStep[] = [];
  const pending = [...ordered];
  while (pending.length) {
    const idx = pending.findIndex((s) => (s.dependsOn ?? []).every((d) => placed.some((p) => p.id === d)));
    placed.push(...pending.splice(idx < 0 ? 0 : idx, 1));
  }
  // Questions and withheld steps of deferred projects belong to the plan that covers them.
  const here = (p?: Id) => !p || !deferred.includes(p);
  const asked = questions.filter((q) => here(q.projectId));
  const held = withheld.filter((w) => here(w.projectId)).map(({ projectId: _p, ...w }) => w);

  const plan: SetupPlan = {
    id,
    scope: { orgId: input.scope.orgId, ...(input.scope.workspaceId ? { workspaceId: input.scope.workspaceId } : {}), projectIds: included },
    createdBy: input.requester.id,
    createdAt: now,
    basedOn: { profileHash: setupInputHash(input), at: now },
    summary: '',
    steps: placed,
    status: 'draft',
    ...(asked.length ? { questions: asked } : {}),
    ...(held.length ? { withheld: held } : {}),
    ...(deferred.length ? { deferredProjectIds: deferred } : {}),
  };
  plan.summary = setupSummary(plan);
  return plan;
}


/** The one sentence the plan card leads with (R-ON-5), from what the plan will do. */
export function setupSummary(plan: Pick<SetupPlan, 'steps'>): string {
  const live = plan.steps.filter((s) => s.state !== 'skipped');
  const parts = ['Agents can work in your repos', 'ask before anything risky'];
  const verifies = live.filter((s) => s.capability === 'landing.configure').map((s) => (s.input as unknown as SetupLandingInput).verify?.mode);
  if (verifies.includes('ci')) parts.push('land through your CI');
  else if (verifies.includes('command')) parts.push('land only when your tests pass');
  if (live.some((s) => s.capability === 'approval-policy.set')) parts.push('need a second person for production');
  return `${parts.slice(0, -1).join(', ')}${parts.length > 2 ? ',' : ''} and ${parts[parts.length - 1]}.`;
}

// ---------------------------------------------------------------------------
// Hashes
// ---------------------------------------------------------------------------

/** The detection a plan came from: profiles, provider facts, runtimes and answers. */
export function setupInputHash(input: SetupInput): string {
  const { profiles, providerFacts, runtimes, codeConnected, machinePaired, hosted, connectedCi, answers, standardsScores } = input;
  return `sha256:${setupSha256Hex(canonicalJson({ profiles, providerFacts, runtimes, codeConnected, machinePaired, hosted, connectedCi, answers, standardsScores }))}`;
}

/**
 * The hash a person approves: the plan id, scope, summary, detection, and every step as they saw
 * it (capability, input, tier, title, why, evidence, dependencies, undo, handoff, ticked or not).
 * Progress (running, done, failed, results), status and the approval itself are left out, so a
 * plan keeps its hash while it runs and any edit changes it.
 */
export function planHash(plan: SetupPlan): string {
  const body = {
    v: 1,
    id: plan.id,
    scope: plan.scope,
    summary: plan.summary,
    basedOn: plan.basedOn.profileHash,
    undoOf: plan.undoOf,
    steps: plan.steps.map((s) => ({
      id: s.id,
      area: s.area,
      capability: s.capability,
      input: s.input,
      tier: s.tier,
      title: s.title,
      why: s.why,
      evidence: s.evidence,
      dependsOn: s.dependsOn ?? [],
      undo: s.undo,
      handoff: s.handoff,
      skipped: s.state === 'skipped',
    })),
  };
  return `sha256:${setupSha256Hex(canonicalJson(body))}`;
}

const K256 = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function utf8Bytes(s: string): number[] {
  const out: number[] = [];
  for (const ch of s) {
    let c = ch.codePointAt(0)!;
    if (c >= 0xd800 && c <= 0xdfff) c = 0xfffd;
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return out;
}

/** SHA-256 of the UTF-8 text, hex. Synchronous and browser-safe, so the console and the hub hash a plan alike. */
export function setupSha256Hex(text: string): string {
  const bytes = utf8Bytes(text);
  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const hi = Math.floor(bitLen / 0x100000000);
  bytes.push((hi >>> 24) & 255, (hi >>> 16) & 255, (hi >>> 8) & 255, hi & 255, (bitLen >>> 24) & 255, (bitLen >>> 16) & 255, (bitLen >>> 8) & 255, bitLen & 255);
  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const w = new Array<number>(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = ((bytes[off + 4 * i]! << 24) | (bytes[off + 4 * i + 1]! << 16) | (bytes[off + 4 * i + 2]! << 8) | bytes[off + 4 * i + 3]!) >>> 0;
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
      const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h as [number, number, number, number, number, number, number, number];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K256[i]! + w[i]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }
  return h.map((x) => x.toString(16).padStart(8, '0')).join('');
}

// ---------------------------------------------------------------------------
// Secrets, validation, edits, the intent check and undo
// ---------------------------------------------------------------------------

const SECRET_KEY_ENDINGS = ['secret', 'token', 'password', 'passwd', 'passphrase', 'apikey', 'privatekey', 'credential', 'credentials', 'accesskey', 'authorization', 'cookie', 'sessionid'];
// Built from parts so the source itself never looks like a key to a repo scanner.
const SECRET_VALUES: readonly RegExp[] = [
  new RegExp('\\bgh[pousr]_[A-Za-z0-9]{20,}'),
  new RegExp('\\bgithub_' + 'pat_[A-Za-z0-9_]{20,}'),
  new RegExp('\\bgl' + 'pat-[A-Za-z0-9_-]{20,}'),
  new RegExp('\\b(s|r)k_(li' + 've|test)_[A-Za-z0-9]{10,}'),
  new RegExp('\\bwh' + 'sec_[A-Za-z0-9+/=]{10,}'),
  new RegExp('\\bAK' + 'IA[0-9A-Z]{16}\\b'),
  new RegExp('-----BEGIN [A-Z ]*PRIVATE ' + 'KEY-----'),
  new RegExp('\\bxox[abprs]-[A-Za-z0-9-]{10,}'),
  new RegExp('\\beyJ[A-Za-z0-9_-]{10,}\\.eyJ[A-Za-z0-9_-]{10,}\\.'),
  new RegExp('\\bsk-(ant-)?[A-Za-z0-9_-]{20,}'),
  new RegExp('\\bAI' + 'za[0-9A-Za-z_-]{35}'),
  new RegExp('[a-z][a-z0-9+.-]*://[^\\s/:@]+:[^\\s/@]+@', 'i'),
];

/**
 * Where a value carries something that looks like a secret: a key named like one with a value, or
 * a value shaped like a known token, private key or URL with a password. Null when clean.
 */
export function findSetupSecret(value: unknown, path = 'input'): string | null {
  if (typeof value === 'string') return SECRET_VALUES.some((r) => r.test(value)) ? path : null;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const hit = findSetupSecret(value[i], `${path}[${i}]`);
      if (hit) return hit;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const norm = k.toLowerCase().replace(/[^a-z]/g, '');
      const filled = (typeof v === 'string' && v.length > 0) || (!!v && typeof v === 'object');
      if (filled && SECRET_KEY_ENDINGS.some((e) => norm.endsWith(e))) return `${path}.${k}`;
      const hit = findSetupSecret(v, `${path}.${k}`);
      if (hit) return hit;
    }
  }
  return null;
}

/** Every project a step input names (projectId fields, project and environment scopes). */
function projectsNamed(value: unknown, out: Set<string> = new Set()): Set<string> {
  if (Array.isArray(value)) for (const v of value) projectsNamed(v, out);
  else if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o.projectId === 'string') out.add(o.projectId);
    if (o.kind === 'project' && typeof o.id === 'string') out.add(o.id);
    for (const v of Object.values(o)) projectsNamed(v, out);
  }
  return out;
}

/** What is wrong with a plan, one line each; empty when it may be shown, approved and run. */
export function validateSetupPlan(plan: SetupPlan): string[] {
  const errors: string[] = [];
  if (!/^stp_[0-9A-Za-z]{1,64}$/.test(plan.id)) errors.push(`Plan id ${JSON.stringify(plan.id)} is not stp_<ulid>.`);
  if (plan.steps.length > SETUP_MAX_STEPS) errors.push(`The plan has ${plan.steps.length} steps; at most ${SETUP_MAX_STEPS} run in one plan.`);
  const scope = new Set(plan.scope.projectIds);
  const seen = new Set<string>();
  for (const s of plan.steps) {
    const at = `Step ${s.id}`;
    if (seen.has(s.id)) errors.push(`${at} appears twice.`);
    if (!SETUP_AREAS.includes(s.area)) errors.push(`${at} has an unknown area.`);
    if (!isSetupCapability(s.capability)) errors.push(`${at} names ${s.capability}, which is not a setup capability.`);
    const handoff = isSetupHandoffCapability(s.capability);
    if (handoff && !s.handoff) errors.push(`${at} is a person's step but has no handoff.`);
    if (!handoff && s.handoff) errors.push(`${at} carries a handoff but is not a handoff capability.`);
    if (s.handoff && !/^(\/(?!\/)|https:\/\/)/.test(s.handoff.url)) errors.push(`${at} hands off to a URL that is neither a console path nor https.`);
    if (tierRankOf(s.tier) < tierRankOf(setupStepTier(s.capability, s.input))) errors.push(`${at} claims tier ${s.tier}, below what its input needs.`);
    for (const d of s.dependsOn ?? []) if (!seen.has(d)) errors.push(`${at} depends on ${d}, which is not an earlier step.`);
    const secret =
      findSetupSecret(s.input) ?? findSetupSecret(s.undo?.input ?? null, 'undo') ?? findSetupSecret(s.handoff ?? null, 'handoff') ?? findSetupSecret([s.title, s.why, ...s.evidence], 'text');
    if (secret) errors.push(`${at} carries what looks like a secret at ${secret}.`);
    for (const p of [...projectsNamed(s.input), ...projectsNamed(s.undo?.input)]) if (!scope.has(p)) errors.push(`${at} names project ${p}, outside the plan.`);
    for (const why of stepInputProblems(s.capability, s.input)) errors.push(`${at} ${why}`);
    if (!plan.undoOf && ('restore' in s.input || 'remove' in s.input)) errors.push(`${at} restores or removes, which only an undo plan does.`);
    if (s.undo && (!isSetupCapability(s.undo.capability) || isSetupHandoffCapability(s.undo.capability))) errors.push(`${at} has an undo that is not a runnable setup capability.`);
    seen.add(s.id);
  }
  return errors;
}

const BINDABLE_ROLES: readonly unknown[] = ['admin', 'operator', 'viewer'];
const INVITE_ROLES: readonly unknown[] = ['operator', 'viewer'];

/**
 * What a step's input may never say, whoever proposed or edited it: a role outside the ones a plan
 * may grant (never owner, in any spelling), a role binding wider than one project, a domain invite
 * for a public mail domain, or an allow rule wider than one exact, plain command.
 */
function stepInputProblems(capability: string, input: Record<string, unknown>): string[] {
  const out: string[] = [];
  const role = input.role;
  if (typeof role === 'string' && role.trim().toLowerCase() === 'owner') out.push('grants owner; a plan never does.');
  if (capability === 'role.bind') {
    if (!BINDABLE_ROLES.includes(role)) out.push('binds a role a plan cannot grant (admin, operator or viewer only).');
    const scope = input.scope as { kind?: unknown; id?: unknown } | undefined;
    if (!scope || scope.kind !== 'project' || typeof scope.id !== 'string') out.push('binds a role outside one project; org-wide roles are set in Settings, Members.');
  }
  if (capability === 'member.invite') {
    if (!INVITE_ROLES.includes(role)) out.push('invites with a role other than operator or viewer.');
    if (typeof input.domain === 'string' && PUBLIC_MAIL_DOMAINS.has(input.domain.trim().toLowerCase().replace(/^@/, ''))) out.push('invites everyone at a public mail domain.');
  }
  if (capability === 'policy.preset.apply' && input.allow !== undefined) {
    const allow = Array.isArray(input.allow) ? (input.allow as unknown[]) : [null];
    for (const r of allow) {
      const rule = (r && typeof r === 'object' ? r : {}) as { tool?: unknown; pattern?: unknown };
      const pattern = typeof rule.pattern === 'string' ? plainCommand(rule.pattern) : null;
      if (rule.tool !== 'Bash' || !pattern || pattern !== rule.pattern || !allowRuleSafe(pattern)) {
        out.push('allows more than one exact, plain command without asking.');
        break;
      }
    }
  }
  return out;
}

export type SetupEditResult = { ok: true; plan: SetupPlan; changed: boolean } | { ok: false; error: UiErrorShape };

const editError = (what: string, why: string, next: string): SetupEditResult => ({ ok: false, error: { what, why, next } });

/**
 * Untick, tick again, or change inputs (PATCH /api/setup/plans/:id). Pure: answers a new plan in
 * 'draft' without its approval, tiers recomputed, so the person must press Apply setup again.
 * Skipping a step skips the steps that depend on it; a step cannot be ticked while one it depends
 * on is unticked. An input may not name another project, carry a secret, grant owner, or (outside
 * an undo plan) restore or remove.
 */
export function applySetupEdit(plan: SetupPlan, edit: SetupPlanEdit): SetupEditResult {
  if (plan.status !== 'draft' && plan.status !== 'approved') return editError('Plan not changed', `The plan is ${plan.status}.`, 'Propose a new plan to change what it does.');
  const byId = new Map(plan.steps.map((s) => [s.id, s]));
  const named = [...(edit.skip ?? []), ...(edit.unskip ?? []), ...Object.keys(edit.inputs ?? {})];
  const unknown = named.find((x) => !byId.has(x));
  if (unknown) return editError('Plan not changed', `The plan has no step ${JSON.stringify(unknown)}.`, 'Reload the plan and try again.');
  const steps = plan.steps.map((s) => ({ ...s }));
  const get = (id: string) => steps.find((s) => s.id === id)!;
  for (const [id, patch] of Object.entries(edit.inputs ?? {})) {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return editError('Plan not changed', `The change to ${id} is not an object.`, 'Send the fields to change as an object.');
    const s = get(id);
    const input = { ...s.input, ...patch };
    const secret = findSetupSecret(input);
    if (secret) return editError('Plan not changed', `The change to ${id} carries what looks like a secret (${secret}). A setup plan never holds one.`, 'Connect credentials yourself through the handoff step instead.');
    const outside = [...projectsNamed(input)].find((p) => !plan.scope.projectIds.includes(p));
    if (outside) return editError('Plan not changed', `The change to ${id} names project ${outside}, which this plan does not cover.`, 'Propose a plan for that project.');
    if (input.role === 'owner') return editError('Plan not changed', 'A setup plan never grants owner.', 'Make someone owner in Settings, Members.');
    if (!plan.undoOf && ('restore' in input || 'remove' in input)) return editError('Plan not changed', `The change to ${id} restores or removes something, which only Undo setup does.`, 'Apply the plan, then use Undo setup.');
    s.input = input;
    s.tier = setupStepTier(s.capability, input);
  }
  for (const id of edit.unskip ?? []) {
    const s = get(id);
    const blocked = (s.dependsOn ?? []).find((d) => get(d).state === 'skipped' && !(edit.unskip ?? []).includes(d));
    if (blocked) return editError('Plan not changed', `${s.title} depends on ${get(blocked).title}, which is unticked.`, 'Tick that step too.');
    if (s.state === 'skipped') s.state = 'proposed';
  }
  const skip = new Set(edit.skip ?? []);
  for (let grew = true; grew; ) {
    grew = false;
    for (const s of steps)
      if (!skip.has(s.id) && (s.dependsOn ?? []).some((d) => skip.has(d))) {
        skip.add(s.id);
        grew = true;
      }
  }
  for (const s of steps) if (skip.has(s.id) && s.state === 'proposed') s.state = 'skipped';
  const next: SetupPlan = { ...plan, steps, status: 'draft' };
  delete next.approval;
  next.summary = setupSummary(next);
  const problems = validateSetupPlan(next);
  if (problems.length) return editError('Plan not changed', problems[0]!, 'Change the step so it stays within what a setup plan may do, or do it in Settings.');
  return { ok: true, plan: next, changed: planHash(next) !== planHash(plan) };
}

/** An agent actor ('agent:assistant'), or anything that is not a person's id. */
const isAgentActor = (by: unknown): boolean => typeof by !== 'string' || !by.trim() || /^agent:/i.test(by.trim());

export type PlanIntentCheck = { ok: true; step: SetupStep } | { ok: false; why: string };

/**
 * The gate's check of a plan-bound intent (see SetupPlanIntent), everything but the approver's
 * current grants, which the hub checks at the step's scope. The plan is the stored one, loaded by
 * `intent.planId`; `call` is the capability and input about to run.
 */
export function verifyPlanIntent(plan: SetupPlan, intent: SetupPlanIntent, call: { capability: string; input: Record<string, unknown> }): PlanIntentCheck {
  const no = (why: string): PlanIntentCheck => ({ ok: false, why });
  if (intent.how !== 'plan') return no('Not a plan-bound intent.');
  if (intent.planId !== plan.id) return no('The intent names another plan.');
  if (!plan.approval) return no('Nobody approved this plan.');
  // A person approves a plan; an agent never approves one, nor runs one on its own behalf.
  if (isAgentActor(plan.approval.by) || isAgentActor(intent.by)) return no('Only a person approves a setup plan.');
  if (plan.status !== 'approved' && plan.status !== 'applying' && plan.status !== 'partial') return no(`The plan is ${plan.status}.`);
  const hash = planHash(plan);
  if (hash !== plan.approval.planHash || hash !== intent.planHash) return no('The plan changed after it was approved.');
  if (intent.by !== plan.approval.by) return no('The intent names someone other than the approver.');
  if (validateSetupPlan(plan).length) return no('The plan is not valid.');
  const step = plan.steps.find((s) => s.id === intent.planStep);
  if (!step) return no('The step is not in the approved plan.');
  if (step.capability !== intent.capability || call.capability !== step.capability) return no('The call is not the step that was approved.');
  if (canonicalJson(call.input) !== canonicalJson(step.input)) return no('The call carries other input than the approved step.');
  if (!isSetupCapability(step.capability) || isSetupHandoffCapability(step.capability)) return no('A person does this step; the assistant never runs it.');
  if (step.state === 'done' || step.state === 'skipped') return no(`The step is ${step.state}; a step runs once.`);
  const pending = (step.dependsOn ?? []).find((d) => plan.steps.find((s) => s.id === d)?.state !== 'done');
  if (pending) return no(`The step waits on ${pending}.`);
  return { ok: true, step };
}

export interface ProposeUndoOptions {
  id?: Id;
  now?: Ts;
  /** Who asks for the undo. */
  by: string;
}

/**
 * Undo setup (ADR 0047 point 3): a new plan with the inverse of every applied step that has one,
 * in reverse plan order (the order they ran), approved and run like any plan. Steps without an inverse (handoffs, pull
 * requests) are listed as withheld with what to do instead.
 */
export function proposeUndo(plan: SetupPlan, opts: ProposeUndoOptions): SetupPlan {
  const now = opts.now ?? Date.now();
  const done = plan.steps.filter((s) => s.state === 'done');
  const steps: SetupStep[] = [];
  const withheld: SetupWithheld[] = [];
  for (const s of [...done].reverse()) {
    if (!s.undo || !isSetupCapability(s.undo.capability) || isSetupHandoffCapability(s.undo.capability)) {
      withheld.push({ capability: s.capability, title: s.title, why: s.handoff ? 'You did this step yourself: undo it where you did it.' : 'This step has no automatic undo: change it in Settings.' });
      continue;
    }
    steps.push({
      id: `undo:${s.id}`,
      area: s.area,
      capability: s.undo.capability,
      input: s.undo.input,
      tier: setupStepTier(s.undo.capability, s.undo.input),
      title: `Undo: ${s.title}`,
      why: `Reverses this step of setup plan ${plan.id}.`,
      evidence: s.result?.auditId ? [`Audit entry ${s.result.auditId}`] : [],
      state: 'proposed',
    });
  }
  const undo: SetupPlan = {
    id: opts.id ?? newSetupPlanId(now),
    scope: plan.scope,
    createdBy: opts.by,
    createdAt: now,
    basedOn: plan.basedOn,
    summary: `Reverses ${steps.length === 1 ? 'one step' : `${steps.length} steps`} of the setup you applied.`,
    steps,
    status: 'draft',
    undoOf: plan.id,
    ...(withheld.length ? { withheld } : {}),
  };
  return undo;
}
